import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineBuildingOffice2,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService } from "../../services";
import masterDataService from "../../services/masterDataService";
import userService from "../../services/userService";
import { toast } from "react-toastify";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";
import "./Departments.css";

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false to show page immediately
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState({
    departmentName: "",
    departmentHeadId: "",
    status: "Active",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load data in background without blocking UI
      loadDepartments(false);
      loadUsers();
    } else {
      setError("Please login to view departments");
    }
  }, [tenantId]);

  const loadDepartments = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError("");
      const data = await masterDataService.getDepartments(tenantId);
      setDepartments(data || []);
    } catch (err) {
      const msg = "Failed to load departments. Please try again.";
      setError(msg);
      toast.error(msg);
      console.error("Error loading departments:", err);
      setDepartments([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const loadUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data.filter((user) => user.status === "Active"));
    } catch (err) {
      console.error("Error loading users:", err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredDepartments = departments.filter((dept) =>
    dept.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingDepartment(null);
    setFormData({
      departmentName: "",
      departmentHeadId: "",
      status: "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      departmentName: department.departmentName || "",
      departmentHeadId: department.departmentHeadId || "",
      status: department.status || "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      await masterDataService.deleteDepartment(id);
      await loadDepartments(false); // Don't show loading on refresh after delete
      setDeleteConfirm(null);
      toast.success("Department deleted successfully!");
    } catch (err) {
      const msg = "Failed to delete department. Please try again.";
      setError(msg);
      toast.error(msg);
      console.error("Error deleting department:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.departmentName.trim()) {
      errors.departmentName = "Department name is required";
    } else if (formData.departmentName.length > 100) {
      errors.departmentName =
        "Department name must be less than 100 characters";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const departmentData = {
        tenantId: tenantId,
        departmentName: formData.departmentName.trim(),
        departmentHeadId: formData.departmentHeadId
          ? parseInt(formData.departmentHeadId)
          : null,
        status: formData.status,
      };

      if (editingDepartment) {
        // Update
        departmentData.departmentId = editingDepartment.departmentId;
        await masterDataService.updateDepartment(
          editingDepartment.departmentId,
          departmentData,
        );
      } else {
        // Create
        await masterDataService.createDepartment(departmentData);
      }

      await loadDepartments(false); // Don't show loading on refresh after save
      setShowModal(false);
      setFormData({
        departmentName: "",
        departmentHeadId: "",
        status: "Active",
      });
      setEditingDepartment(null);
      toast.success(editingDepartment ? "Department updated successfully!" : "Department created successfully!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save department. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDepartment(null);
    setFormData({ departmentName: "", departmentHeadId: "", status: "Active" });
    setFormErrors({});
  };

  return (
    <div className="pagecontainer departments-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Departments</h1>
          <p>Manage your organization departments</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary btn" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Department
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10 kizantable">
        <div className="table-header">
          <div className="left-group">
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search departments..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
            <div className="table-info">
              {loading && departments.length === 0 ? (
                <PageLoading variant="muted" message="Loading…" />
              ) : (
                `${filteredDepartments.length} ${filteredDepartments.length === 1 ? "department" : "departments"}`
              )}
            </div>
          </div>
        </div>

        {loading && departments.length === 0 ? (
          <div className="table-loading">
            <PageLoading variant="compact" message="Loading departments…" />
          </div>
        ) : filteredDepartments.length === 0 ? (
          <div className="empty-state">
            <HiOutlineBuildingOffice2 className="empty-icon" />
            <h3>No departments found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first department"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                New Department
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Department Name</th>
                <th>Department Head</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepartments.map((dept, index) => (
                <tr key={dept.departmentId}>
                  <td>{index + 1}</td>
                  <td className="name-cell">{dept.departmentName}</td>
                  <td>{dept.departmentHeadName || "-"}</td>
                  <td>
                    <span
                      className={`status-badge ${dept.status?.toLowerCase()}`}
                    >
                      {dept.status || "Active"}
                    </span>
                  </td>
                  <td>
                    {dept.createdAt
                      ? new Date(dept.createdAt).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(dept)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(dept)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingDepartment ? "Edit Department" : "Add New Department"}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="department-form"
              className="btn-primary btn"
              disabled={submitting}
            >
              {submitting
                ? editingDepartment
                  ? "Updating..."
                  : "Creating..."
                : editingDepartment
                  ? "Update"
                  : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body departmentform">
          <form id="department-form" onSubmit={handleSubmit} className="modal-form">
            <div className="row" style={{display: 'flex', flexDirection: 'column'}}>
              <div className="form-group">
                <label htmlFor="departmentName">
                  Department Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="departmentName"
                  value={formData.departmentName}
                  onChange={(e) =>
                    setFormData({ ...formData, departmentName: e.target.value })
                  }
                  className={formErrors.departmentName ? "error" : ""}
                  placeholder="Enter department name"
                  maxLength={100}
                />
                {formErrors.departmentName && (
                  <span className="field-error">
                    {formErrors.departmentName}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="departmentHeadId">Department Head</label>
                <select
                  id="departmentHeadId"
                  value={formData.departmentHeadId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      departmentHeadId: e.target.value,
                    })
                  }
                >
                  <option value="">Select Department Head</option>
                  {users.map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="400px"
        customClass="delete-modal"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm.departmentId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.departmentName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The department will be marked as
            inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Departments;
